import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import DoneIcon from "@mui/icons-material/Done";
import CachedIcon from "@mui/icons-material/Cached";
import NoTransferIcon from "@mui/icons-material/NoTransfer";
import { loadSuggestedUnits } from "redux/actions/unitsSection/unitsAction";
import { generateUUID } from "helpers/generateUUID";
import { findEtaForUnit, combineSuggestions } from "helpers/suggestionUtils";

const DEFAULT_SUGGESTED_UNITS = 3;
const MAX_RETRIES = 2;

export { MAX_RETRIES, combineSuggestions };

export const useSuggestions = ({
  account,
  inputValues,
  products,
  selectedBrand,
  stationUnits,
  unitsOpt,
  setUnitsOpt,
  validatedSchedules,
  handleNewValues,
  setSelectedUnit,
  selectedUnit,
}) => {
  const dispatch = useDispatch();
  const { id, address, idStation, idCustomer, localPhone } = account || {};
  const { myCallID } = useSelector(({ customersReducer }) => customersReducer);
  const { suggestedUnits, suggestionFailed } = useSelector(
    ({ unitsReducer }) => unitsReducer,
  );
  const { stationConfig } = useSelector(({ configSync }) => configSync);

  const [suggestionValues, setSuggestionValues] = useState({});
  const [suggestionStatus, setSuggestionStatus] = useState({ show: false });
  const [suggestedUnitInfo, setSuggestedUnitInfo] = useState(null);
  const [allSuggestions, setAllSuggestions] = useState([]);
  const [suggestedDate, setSuggestedDate] = useState(null);
  const [suggestedSchedule, setSuggestedSchedule] = useState(null);
  const [hasSuggestedSchedule, setHasSuggestedSchedule] = useState(false);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [requestId, setRequestId] = useState(generateUUID());
  const [isSuggestedUnit, setIsSuggestedUnit] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [hadPreselectedUnit, setHadPreselectedUnit] = useState(false);

  const processedSuggestionsRef = useRef(null);

  const resetSuggestionState = useCallback((extras = {}) => {
    setSuggestionStatus({ show: false });
    setSuggestedUnitInfo(null);
    setHasSuggestedSchedule(false);
    setAllSuggestions([]);
    setSuggestedDate(null);
    setSuggestedSchedule(null);
    setHasAutoSelected(false);
    setRetryCount(0);
    setIsSuggestedUnit(false);
    setHadPreselectedUnit(false);
    setIsRetrying(false);
    setRequestId(generateUUID());
    processedSuggestionsRef.current = null;
    Object.entries(extras).forEach(([key, value]) => {
      const setters = {
        retryCount: setRetryCount,
        hasAutoSelected: setHasAutoSelected,
        hadPreselectedUnit: setHadPreselectedUnit,
        isRetrying: setIsRetrying,
      };
      if (setters[key]) setters[key](value);
    });
  }, []);

  const clearUnitSuggestions = useCallback(() => {
    setUnitsOpt((prev) =>
      prev.length > 0
        ? prev.map(({ suggested: _, ...rest }) => rest)
        : prev
    );
  }, [setUnitsOpt]);

  const dispatchLoadSuggestions = useCallback(
    (overrides = {}) => {
      const { quantity, date, schedule } = { ...inputValues, ...overrides };
      dispatch(
        loadSuggestedUnits(
          quantity,
          `${address.street} ${address.extNumber}, Colonia ${address.colony} ${address.postalCode} ${address.municipality} ${address.state}`,
          address.latitude,
          address.longitude,
          products.find((prod) => prod.id === inputValues.product)?.product,
          selectedBrand && selectedBrand?.name !== "Todos"
            ? selectedBrand.name
            : stationUnits.find((obj) => obj.brand)?.brand,
          date || validatedSchedules[0]?.value,
          schedule?.split("-")[0] ||
            validatedSchedules[0]?.schedules[0]?.start,
          idStation,
          myCallID,
          requestId,
          stationConfig?.numSuggestions || DEFAULT_SUGGESTED_UNITS,
          null,
          id,
          idCustomer,
          localPhone,
        ),
      );
    },
    [
      inputValues,
      address,
      products,
      selectedBrand,
      stationUnits,
      validatedSchedules,
      idStation,
      myCallID,
      requestId,
      stationConfig,
      id,
      idCustomer,
      localPhone,
      dispatch,
    ],
  );

  // Handle suggestion failure
  useEffect(() => {
    if (suggestionFailed) {
      setSuggestionStatus({
        show: true,
        status: "error",
        text: "Error al buscar sugerencias",
        icon: <CachedIcon color="error" />,
      });
    }
  }, [suggestionFailed]);

  useEffect(() => {
    if (suggestedUnits && suggestedUnits.error) {
      setSuggestionStatus({
        show: true,
        status: "error",
        text: "Error al buscar sugerencias",
        icon: <CachedIcon color="error" />,
      });
    }
  }, [suggestedUnits]);

  // Trigger suggestion loading when quantity/product/brand change
  useEffect(() => {
    if (
      !stationConfig?.useRapidCanvas ||
      !suggestionValues.quantity ||
      suggestionValues.quantity <= 0 ||
      !inputValues.product ||
      !selectedBrand ||
      !address ||
      !stationUnits.length
    ) {
      setSuggestionStatus({ show: false });
      setSuggestedUnitInfo(null);
      setHasSuggestedSchedule(false);
      setAllSuggestions([]);
      return;
    }

    setSuggestedUnitInfo(null);
    setHasSuggestedSchedule(false);
    setIsSuggestedUnit(false);

    const statusTimeoutId = setTimeout(() => {
      setSuggestionStatus({
        show: true,
        status: "info",
        text: "Generando sugerencias...",
        icon: <CircularProgress size="18px" color="info" />,
      });
    }, 50);

    dispatchLoadSuggestions({ quantity: suggestionValues.quantity });

    return () => clearTimeout(statusTimeoutId);
  }, [suggestionValues.quantity, inputValues.product, selectedBrand]);

  // Process suggested units when they arrive
  useEffect(() => {
    if (!suggestedUnits.camion) return;

    const newSuggestion = {
      attempt: allSuggestions.length + 1,
      camion: suggestedUnits.camion,
      fecha: suggestedUnits.fecha,
      hora: suggestedUnits.hora,
      etaMax: suggestedUnits.etaMax || null,
      responseTime: suggestedUnits.responseTime || null,
      statusCode: suggestedUnits.statusCode || null,
      requestBody: suggestedUnits.requestBody || null,
      definitiveReason: suggestedUnits.definitiveReason || null,
    };
    const updatedAllSuggestions = [...allSuggestions, newSuggestion];
    setAllSuggestions(updatedAllSuggestions);

    const hasValidSuggestions = Object.values(suggestedUnits.camion).some(
      (value) => value !== null,
    );
    if (!hasValidSuggestions) {
      setSuggestionStatus({
        show: true,
        status: "warning",
        text: "Sin coincidencias",
        definitiveReason: suggestedUnits.definitiveReason,
        icon: <NoTransferIcon color="warning" />,
      });
      return;
    }

    const filteredUnits =
      selectedBrand && selectedBrand.idBrand !== "todos"
        ? unitsOpt.filter((unit) => unit.idBrand === selectedBrand.idBrand)
        : unitsOpt;

    if (!filteredUnits || filteredUnits.length === 0) return;

    const suggestionsKey = JSON.stringify(suggestedUnits);
    if (processedSuggestionsRef.current === suggestionsKey) return;
    processedSuggestionsRef.current = suggestionsKey;

    let allCombinedUnits = [...Object.values(suggestedUnits.camion)];
    let allCombinedDates = [...Object.values(suggestedUnits.fecha)];
    let allCombinedTimes = [...Object.values(suggestedUnits.hora)];

    updatedAllSuggestions.forEach((suggestion) => {
      allCombinedUnits = [
        ...allCombinedUnits,
        ...Object.values(suggestion.camion),
      ];
      allCombinedDates = [
        ...allCombinedDates,
        ...Object.values(suggestion.fecha),
      ];
      allCombinedTimes = [
        ...allCombinedTimes,
        ...Object.values(suggestion.hora),
      ];
    });

    let foundSuggestion = false;
    let foundSchedule;
    const updatedUnits = filteredUnits
      .map((obj) => {
        const { suggested: _, ...rest } = obj;
        for (const suggestedNumber of allCombinedUnits) {
          if (
            suggestedNumber &&
            (rest.number === suggestedNumber ||
              rest.number?.includes(suggestedNumber) ||
              suggestedNumber?.includes(rest.number) ||
              rest.number?.toLowerCase() === suggestedNumber?.toLowerCase())
          ) {
            foundSuggestion = true;
            if (!foundSchedule)
              foundSchedule = {
                date:
                  suggestedUnits.fecha[Object.keys(suggestedUnits.camion)[0]],
                time:
                  suggestedUnits.hora[Object.keys(suggestedUnits.camion)[0]],
              };
            return { ...rest, suggested: true };
          }
        }
        return rest;
      })
      .sort((a, b) => !!b.suggested - !!a.suggested);

    const allUpdatedUnits = unitsOpt.map((unit) => {
      const filteredUnit = updatedUnits.find((u) => u.id === unit.id);
      if (filteredUnit) return filteredUnit;
      const { suggested: _suggested, ...cleanUnit } = unit;
      return cleanUnit;
    });

    const firstKey = Object.keys(suggestedUnits.camion)[0];
    const suggestedUnitData = {
      unit: suggestedUnits.camion[firstKey],
      date: suggestedUnits.fecha[firstKey],
      time: suggestedUnits.hora[firstKey],
      etaMax: suggestedUnits.etaMax?.[firstKey] || null,
    };

    setUnitsOpt(allUpdatedUnits);
    setSuggestionStatus({
      show: true,
      status: "success",
      text: "Sugerencias encontradas",
      icon: <DoneIcon color="success" />,
    });
    setSuggestedUnitInfo(suggestedUnitData);
    setSuggestedDate(suggestedUnitData.date);
    setSuggestedSchedule(suggestedUnitData.time);

    const reorderTimeoutId = setTimeout(() => {
      const reorderedUnits = [...allUpdatedUnits].sort(
        (a, b) => !!b.suggested - !!a.suggested,
      );
      setUnitsOpt(reorderedUnits);
    }, 50);

    setIsSuggestedUnit(true);

    if (!foundSchedule && Object.keys(suggestedUnits.fecha).length > 0) {
      foundSchedule = {
        date: suggestedUnits.fecha["0"],
        time: suggestedUnits.hora["0"],
      };
    }

    let autoSelectTimeoutId;
    let innerTimeoutId;

    // Auto-select suggested date and schedule
    if (
      suggestedUnitData.date &&
      !hasAutoSelected &&
      validatedSchedules.length > 0
    ) {
      autoSelectTimeoutId = setTimeout(() => {
        const suggestedDateSchedules = validatedSchedules.find(
          (sch) => sch.value === suggestedUnitData.date,
        );

        if (suggestedDateSchedules) {
          let suggestedScheduleValue = null;
          if (suggestedUnitData.time) {
            const suggestedHour = parseInt(
              suggestedUnitData.time.split(":")[0],
            );
            const matchingSchedule = suggestedDateSchedules.schedules.find(
              (sch) => {
                if (!sch.value) return false;
                const [startTime, endTime] = sch.value.split(" - ");
                const startHour = parseInt(startTime?.split(":")[0]);
                const endHour = parseInt(endTime?.split(":")[0]);
                return suggestedHour === startHour || suggestedHour === endHour;
              },
            );
            if (matchingSchedule) {
              suggestedScheduleValue = matchingSchedule.value;
            }
          }

          const finalScheduleValue =
            suggestedScheduleValue ||
            suggestedDateSchedules.schedules[0]?.value;

          innerTimeoutId = setTimeout(() => {
            let unitToSet = null;
            let updatedUnit = null;
            const shouldApplySuggestedUnit = isRetrying || !hadPreselectedUnit;

            if (foundSuggestion && shouldApplySuggestedUnit) {
              const suggestedUnit = updatedUnits.find((obj) => obj.suggested);
              if (suggestedUnit) {
                updatedUnit = suggestedUnit;
                setIsSuggestedUnit(!!suggestedUnit?.suggested);
                unitToSet = suggestedUnit.id;
              }
            }

            if (suggestedDateSchedules)
              // setDates handled externally
              setHasSuggestedSchedule(!!suggestedScheduleValue);

            if (updatedUnit) setSelectedUnit(updatedUnit);

            handleNewValues({
              ...inputValues,
              date: suggestedUnitData.date,
              schedule: finalScheduleValue,
              ...(unitToSet && { idUnit: unitToSet }),
            });
          }, 100);
        }
      }, 300);

      setHasAutoSelected(true);
    }

    return () => {
      clearTimeout(reorderTimeoutId);
      if (autoSelectTimeoutId) clearTimeout(autoSelectTimeoutId);
      if (innerTimeoutId) clearTimeout(innerTimeoutId);
    };
  }, [suggestedUnits, validatedSchedules]);

  // Debounced trigger when quantity changes
  useEffect(() => {
    if (!inputValues.quantity || inputValues.quantity <= 0 || !account || !id)
      return;

    const delayDebounceFn = setTimeout(() => {
      if (!isRetrying && allSuggestions.length === 0) {
        setHadPreselectedUnit(!!selectedUnit);
      }
      setSuggestionValues((prev) => ({
        ...prev,
        quantity: inputValues.quantity,
      }));
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [inputValues.quantity, account, id]);

  // Reset on account change
  useEffect(() => {
    if (!account) return;
    resetSuggestionState();
    // eslint-disable-next-line
  }, [account?.id]);

  // Reset on quantity/product/brand change
  useEffect(() => {
    resetSuggestionState({
      hadPreselectedUnit: hadPreselectedUnit,
      isRetrying: isRetrying,
    });
    clearUnitSuggestions();

    if (
      selectedUnit &&
      selectedBrand &&
      selectedBrand.idBrand !== "todos" &&
      selectedUnit.idBrand !== selectedBrand.idBrand
    ) {
      setSelectedUnit(null);
      handleNewValues({ ...inputValues, idUnit: null });
    }
  }, [inputValues.quantity, inputValues.product, selectedBrand]);

  const handleRetry = useCallback(() => {
    if (retryCount >= MAX_RETRIES || !address) return;

    setRetryCount((prev) => prev + 1);
    setIsRetrying(true);
    setHasAutoSelected(false);
    setSuggestionStatus({
      show: true,
      status: "info",
      text: "Generando sugerencias...",
      icon: <CircularProgress size="18px" color="info" />,
    });

    dispatchLoadSuggestions();
  }, [retryCount, address, dispatchLoadSuggestions]);

  const handleUnitChange = useCallback(
    (value) => {
      setIsSuggestedUnit(!!value?.suggested);

      const matchedEta = findEtaForUnit(value, allSuggestions);
      setSuggestedUnitInfo((prev) =>
        prev ? { ...prev, etaMax: matchedEta } : null,
      );

      if (value && !isRetrying && allSuggestions.length === 0) {
        setHadPreselectedUnit(true);
      }
    },
    [allSuggestions, isRetrying],
  );

  return {
    suggestionStatus,
    suggestedUnitInfo,
    allSuggestions,
    suggestedDate,
    suggestedSchedule,
    hasSuggestedSchedule,
    isSuggestedUnit,
    retryCount,
    isRetrying,
    handleRetry,
    handleUnitChange,
    resetSuggestionState,
    clearUnitSuggestions,
    stationConfig,
  };
};
